import { useState } from "react";
import { Dialog } from "./Dialog";
import { Button } from "./Button";
import { CheckCircle2, Copy, ExternalLink, Globe } from "lucide-react";

export interface PublishSuccessModalProps {
  open: boolean;
  onClose: () => void;
  siteName: string;
  slug: string;
  versionNumber?: number;
  onGoToDashboard?: () => void;
}

export function PublishSuccessModal({
  open,
  onClose,
  siteName,
  slug,
  versionNumber,
  onGoToDashboard,
}: PublishSuccessModalProps) {
  const [copied, setCopied] = useState(false);

  const publicUrl = `${window.location.origin}/s/${slug}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback
      setCopied(false);
    }
  };

  const handleVisit = () => {
    window.open(publicUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Website Published Successfully!"
      footer={
        <div style={{ display: "flex", width: "100%", justifyContent: "space-between", gap: "12px" }}>
          {onGoToDashboard ? (
            <Button variant="secondary" onClick={onGoToDashboard}>
              Back to Dashboard
            </Button>
          ) : (
            <div />
          )}
          <Button variant="primary" onClick={handleVisit}>
            <ExternalLink size={16} />
            Visit Live Site
          </Button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: "8px 0" }}>
        <div
          style={{
            width: "56px",
            height: "56px",
            borderRadius: "50%",
            background: "#dcfce7",
            color: "#16a34a",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "16px",
          }}
        >
          <CheckCircle2 size={32} />
        </div>

        <h3 style={{ fontSize: "1.25rem", fontWeight: 700, color: "#0f172a", margin: "0 0 6px 0" }}>
          {siteName} is now live!
        </h3>
        {versionNumber ? (
          <p style={{ fontSize: "0.875rem", color: "#64748b", margin: "0 0 20px 0" }}>
            Published as <strong>Version {versionNumber}</strong>
          </p>
        ) : (
          <p style={{ fontSize: "0.875rem", color: "#64748b", margin: "0 0 20px 0" }}>
            Your website is accessible to anyone on the web.
          </p>
        )}

        <div style={{ width: "100%", textAlign: "left", marginBottom: "16px" }}>
          <label style={{ display: "block", fontSize: "0.8125rem", fontWeight: 600, color: "#475569", marginBottom: "6px" }}>
            Public Website Link
          </label>
          <div style={{ display: "flex", gap: "8px" }}>
            <div style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              background: "#f1f5f9",
              border: "1px solid #cbd5e1",
              borderRadius: "6px",
              fontSize: "0.875rem",
              color: "#1e293b",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}>
              <Globe size={15} style={{ color: "#64748b", flexShrink: 0 }} />
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{publicUrl}</span>
            </div>
            <Button variant="secondary" onClick={handleCopy}>
              <Copy size={15} />
              {copied ? "Copied!" : "Copy"}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
